import DeleteIcon from '@mui/icons-material/Delete';
import UploadIcon from '@mui/icons-material/CloudUpload';
import { Alert, Box, Button, CircularProgress, Stack, Typography } from '@mui/material';
import { useRef, useState } from 'react';
import { useInput, useNotify } from 'react-admin';
import { callAction } from './dataProvider';
import { FileBanner, FileImage } from './image';

/**
 * An image field backed by FileJet.
 *
 * Three steps, deliberately: ask the API for an upload instruction, PUT the
 * bytes straight to the URL it returns, then store the identifier on the
 * record. The file never travels through the API, which is what makes a photo
 * of a shop interior possible at all - API Gateway refuses a request body over
 * 10 MB, and a Lambda would be paying to hold the bytes in memory either way.
 *
 * The identifier is written into the form as soon as the upload lands, but the
 * record is only saved when the form is. An upload the editor then abandons
 * leaves a file nobody references; that is the cheaper mistake by far compared
 * with deleting a file that a record still points at.
 */

interface UploadInstruction {
  readonly id: string;
  readonly url: string;
  readonly method: string;
  readonly headers: Record<string, string>;
}

const MAX_BYTES = 10 * 1024 * 1024;

/*
 * Both the media types and the extensions.
 *
 * The extensions matter: a picker filtering on media type alone greys out
 * files whose type the operating system does not know - .jfif being the common
 * one, which is what Windows produces from "save image as". The server decides
 * either way; this only governs what is selectable.
 */
const ACCEPTED = [
  'image/png', 'image/jpeg', 'image/webp', 'image/gif', 'image/avif',
  '.jpg', '.jpeg', '.jpe', '.jfif', '.png', '.webp', '.gif', '.avif'
].join(',');

export function FilejetImageInput({
  source,
  label = 'Obrázok',
  helperText,
  size = 120,
  preview = 'square'
}: {
  source: string;
  label?: string;
  helperText?: string;
  size?: number;
  /** 'wide' shows the image at its own aspect ratio, for banners you need to read. */
  preview?: 'square' | 'wide';
}) {
  const { field } = useInput({ source });
  const notify = useNotify();
  const input = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const identifier = typeof field.value === 'string' && field.value !== '' ? field.value : null;

  const upload = async (file: File) => {
    setError(null);

    if (file.size > MAX_BYTES) {
      setError(`Súbor má ${formatSize(file.size)}. Maximum je ${formatSize(MAX_BYTES)}.`);
      return;
    }

    setBusy(true);
    try {
      const instruction = (await callAction('uploads', 'POST', {
        contentType: file.type,
        filename: file.name
      })) as UploadInstruction;

      const response = await fetch(instruction.url, {
        method: instruction.method,
        headers: instruction.headers,
        body: file
      });

      if (!response.ok) {
        throw new Error(`Storage answered ${response.status}.`);
      }

      field.onChange(instruction.id);
      notify('Obrázok nahraný. Uložte formulár, aby sa priradil.', { type: 'info' });
    } catch (cause) {
      console.error('Upload failed', cause);
      // The API says exactly what is wrong - which file type, or that it is
      // too large. Replacing that with "try again" is what turned a refused
      // file type into a bug report with nothing to go on.
      setError(messageFor(cause));
    } finally {
      setBusy(false);
      // Let the same file be picked again after a failure.
      if (input.current != null) input.current.value = '';
    }
  };

  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 1 }}>
        {label}
      </Typography>

      <Stack direction={preview === 'wide' ? 'column' : 'row'} spacing={2} alignItems="flex-start">
        {identifier == null ? (
          <Box
            sx={{
              width: preview === 'wide' ? Math.min(size * 4, 640) : size,
              height: size,
              border: '1px dashed',
              borderColor: 'divider',
              borderRadius: 1,
              display: 'grid',
              placeItems: 'center'
            }}
          >
            <Typography variant="caption" color="text.secondary">
              bez obrázka
            </Typography>
          </Box>
        ) : preview === 'wide' ? (
          <FileBanner id={identifier} width={640} alt={label} />
        ) : (
          <FileImage id={identifier} size={size} alt={label} />
        )}

        <Stack spacing={1} alignItems="flex-start">
          <Button
            size="small"
            variant="outlined"
            startIcon={busy ? <CircularProgress size={16} /> : <UploadIcon />}
            disabled={busy}
            onClick={() => input.current?.click()}
          >
            {identifier == null ? 'Nahrať obrázok' : 'Nahradiť'}
          </Button>

          {identifier != null && (
            <Button
              size="small"
              color="error"
              startIcon={<DeleteIcon />}
              disabled={busy}
              onClick={() => field.onChange(null)}
            >
              Odstrániť
            </Button>
          )}

          {helperText != null && (
            <Typography variant="caption" color="text.secondary">
              {helperText}
            </Typography>
          )}
        </Stack>
      </Stack>

      <input
        ref={input}
        type="file"
        accept={ACCEPTED}
        hidden
        onChange={event => {
          const file = event.target.files?.[0];
          if (file != null) void upload(file);
        }}
      />

      {error != null && (
        <Alert severity="warning" variant="outlined" sx={{ mt: 1 }}>
          {error}
        </Alert>
      )}
    </Box>
  );
}

function formatSize(bytes: number): string {
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * The reason an upload failed, in the words the API used.
 *
 * react-admin's fetch helper throws an error carrying the response body's
 * `message`, which for uploads is a sentence written for the person reading
 * it. Anything without one - the network dropped, storage refused the bytes -
 * falls back to something generic, because there is nothing better to say.
 */
export function messageFor(cause: unknown): string {
  const message = (cause as { message?: unknown }).message;
  return typeof message === 'string' && message.trim() !== '' && !/^\d+$/.test(message)
    ? message
    : 'Obrázok sa nepodarilo nahrať. Skúste to znova.';
}
